/**
 * Application-wide constants
 * Centralizes all magic strings, configuration values, and constant data
 */

// ============================================================================
// DEFAULT VALUES
// ============================================================================

/**
 * Default month for financial data (March)
 */
export const DEFAULT_MONTH = '03' as const;

/**
 * Default year for financial data
 */
export const DEFAULT_YEAR = '2025' as const;

/**
 * Default left year for timeline selector
 */
export const DEFAULT_TIMELINE_LEFT = '19' as const;

/**
 * Default right year for timeline selector
 */
export const DEFAULT_TIMELINE_RIGHT = '25' as const;

/**
 * Default period type for financial data
 */
export const DEFAULT_PERIOD_TYPE = 'Annual' as const;

// ============================================================================
// VALID VALUES
// ============================================================================

/**
 * Valid month values (01-12)
 */
export const MONTHS = [
  '01', '02', '03', '04', '05', '06',
  '07', '08', '09', '10', '11', '12'
] as const;

/**
 * Month names for display
 */
export const MONTH_NAMES = {
  '01': 'January',
  '02': 'February',
  '03': 'March',
  '04': 'April',
  '05': 'May',
  '06': 'June',
  '07': 'July',
  '08': 'August',
  '09': 'September',
  '10': 'October',
  '11': 'November',
  '12': 'December',
} as const;

/**
 * Valid years for financial data selection
 */
export const DATA_YEARS = ['2023', '2024', '2025'] as const;

/**
 * Timeline years (abbreviated format)
 * Represents fiscal years from 1999 to 2025
 */
export const TIMELINE_YEARS = [
  '99', '01', '03', '05', '07', '09', '11', '13',
  '15', '17', '19', '21', '23', '25'
] as const;

/**
 * Valid period types for financial data
 */
export const PERIOD_TYPES = [
  'Annual',
  'Quarterly',
  'Semi-Annual',
  'LTM'
] as const;

// ============================================================================
// TAB IDENTIFIERS
// ============================================================================

/**
 * Tab IDs for the main navigation
 */
export const TAB_IDS = {
  FINANCIAL: 'financial',
  TRADING: 'trading',
  OPERATING: 'operating',
  BUSINESS: 'business',
} as const;

/**
 * Tab names with suffix (for DOM IDs)
 */
export const TAB_NAMES = {
  FINANCIAL: 'financial-tab',
  TRADING: 'trading-tab',
  OPERATING: 'operating-tab',
  BUSINESS: 'business-tab',
} as const;

// ============================================================================
// CSS SELECTORS
// ============================================================================

/**
 * CSS class selectors for common elements
 */
export const SELECTORS = {
  // Date controls
  DATE_SELECTOR: '.date-selector',
  MONTH_SELECT: '.month-select',
  YEAR_SELECT: '.year-select',
  DATE_GO_BUTTON: '.date-go-button',

  // Tabs
  TAB_CONTENT: '.tab-content',
  TAB: '.tab',

  // Tables
  COMPS_TABLE: '.comps-table',
  SUMMARY_TABLE: '.summary-table',

  // Options dropdown
  OPTIONS_BUTTON: '.options-button',
  OPTIONS_DROPDOWN: '.options-dropdown',
  OPTION_ITEM: '.option-item',

  // Download
  DOWNLOAD_BUTTON: '.download-button',

  // Timeline
  TIMELINE_TRACK: '.timeline-track',
  TIMELINE_SELECTION: '.timeline-selection',
  TIMELINE_HANDLE_LEFT: '.timeline-handle-left',
  TIMELINE_HANDLE_RIGHT: '.timeline-handle-right',
  TIMELINE_YEAR_LABEL: '.timeline-year-label',

  // Period type control
  CONTROL_SELECT: '.control-select',
  PERIOD_TYPE_INDICATOR: '.period-type-indicator',

  // Business description toggles
  TOGGLE_DESC: '.toggle-desc',
  BLUE_LINK: '.blue-link',

  // Utility classes
  ACTIVE: '.active',
  SHOW: '.show',
  HIDDEN: '.hidden',
} as const;

/**
 * CSS class names (without the dot)
 */
export const CLASS_NAMES = {
  ACTIVE: 'active',
  SHOW: 'show',
  HIDDEN: 'hidden',
  TAB_CONTENT: 'tab-content',
  COMPS_TABLE: 'comps-table',
  SUMMARY_TABLE: 'summary-table',
} as const;

// ============================================================================
// FILE PATHS
// ============================================================================

/**
 * File paths for downloads
 * Note: Vite serves public/ folder at root, so paths are absolute from root
 */
export const FILE_PATHS = {
  COMPS_2023: '/files/Comps[2023].xlsx',
  COMPS_2024: '/files/Comps[2024].xlsx',
  COMPS_2025: '/files/Comps[2025].xlsx',
  CAPIQ_DEFAULT: '/files/NVIDIA Corporation NasdaqGS NVDA Financials.xlsx',
  CAPIQ_FY23: '/files/capiq-FY23.xlsx',
  NVIDIA_FY14: '/files/nvidia-FY14.xlsx',
} as const;

/**
 * Sidebar HTML path
 */
export const SIDEBAR_PATH = 'sidebar.html';

// ============================================================================
// TARGET COMPANIES
// ============================================================================

/**
 * Target companies for specific download criteria
 * These companies must match exactly (sorted) for the special download files
 */
export const TARGET_COMPANIES = [
  'Advanced Micro Devices, Inc. (NasdaqGS:AMD)',
  'Broadcom Inc. (NasdaqGS:AVGO)',
  'QUALCOMM Incorporated (NasdaqGS:QCOM)',
  'Micron Technology, Inc. (NasdaqGS:MU)',
  'Microsoft Corporation (NasdaqGS:MSFT)',
].sort();

// ============================================================================
// LOCAL STORAGE KEYS
// ============================================================================

/**
 * LocalStorage keys for persisting state
 */
export const STORAGE_KEYS = {
  TIMELINE_RANGE: 'timelineRange',
  PERIOD_TYPE: 'periodType',
} as const;

// ============================================================================
// DATA ATTRIBUTE NAMES
// ============================================================================

/**
 * HTML data attribute names
 */
export const DATA_ATTRIBUTES = {
  TAB: 'data-tab',
  COMPANY: 'data-company',
  STATE: 'data-state',
  TYPE: 'data-type',
} as const;

// ============================================================================
// SPECIAL VALUES
// ============================================================================

/**
 * Special values used in financial data
 */
export const SPECIAL_VALUES = {
  DASH: '-',               // Missing or unavailable data
  NOT_MEANINGFUL: 'NM',   // Not meaningful (e.g., negative P/E)
  ZERO: '0',
  EMPTY: '',
} as const;

// ============================================================================
// TABLE COLUMN INDICES
// ============================================================================

/**
 * Column indices for financial table
 * (0-indexed, including checkbox column)
 */
export const FINANCIAL_COLUMNS = {
  CHECKBOX: 0,
  COMPANY_NAME: 1,
  PRICE: 2,
  SHARES: 3,
  MARKET_CAP: 4,
  DEBT: 5,
  PREF_EQUITY: 6,
  MINORITY_INTEREST: 7,
  ENTERPRISE_VALUE: 8,
  BOOK_VALUE: 9,
  FILING_DATE: 10,
  REVENUE: 11,
  EBITDA: 12,
  EBIT: 13,
  EPS: 14,
  NTM_REVENUE: 15,
  NTM_EBITDA: 16,
  NTM_EPS: 17,
} as const;

/**
 * Summary row names for statistics
 */
export const SUMMARY_ROWS = {
  HIGH: 'High',
  LOW: 'Low',
  MEAN: 'Mean',
  MEDIAN: 'Median',
} as const;

// ============================================================================
// URL PARAMETERS
// ============================================================================

/**
 * URL query parameter names
 */
export const URL_PARAMS = {
  MONTH: 'month',
  YEAR: 'year',
} as const;

// ============================================================================
// EVENT NAMES
// ============================================================================

/**
 * Custom event names for inter-component communication
 */
export const EVENTS = {
  TAB_CHANGED: 'tabChanged',
  DATE_CHANGED: 'dateChanged',
  TIMELINE_CHANGED: 'timelineChanged',
  PERIOD_TYPE_CHANGED: 'periodTypeChanged',
  DATA_LOADED: 'dataLoaded',
  COMPANIES_DELETED: 'companiesDeleted',
} as const;

// ============================================================================
// TOGGLE STATES
// ============================================================================

/**
 * Toggle states for business description/headquarters
 */
export const TOGGLE_STATES = {
  SHORT: 'short',
  FULL: 'full',
} as const;

/**
 * Toggle types
 */
export const TOGGLE_TYPES = {
  DESCRIPTION: 'description',
  HEADQUARTERS: 'headquarters',
} as const;

// ============================================================================
// CHART CONFIGURATION
// ============================================================================

/**
 * Chart-related constants
 */
export const CHART_CONFIG = {
  MIN_COMPANIES: 1,
  MAX_COMPANIES: 10,
  DEFAULT_COLOR_SCHEME: 'category10',
} as const;

// ============================================================================
// VALIDATION RULES
// ============================================================================

/**
 * Validation rules for user inputs
 */
export const VALIDATION = {
  MIN_YEAR: 1999,
  MAX_YEAR: 2025,
  REQUIRED_MONTH: '03',  // Only March data is available
} as const;

// ============================================================================
// UI TEXT
// ============================================================================

/**
 * Common UI text strings
 */
export const UI_TEXT = {
  DELETE_CONFIRMATION_SINGLE: 'Are you sure you want to delete the selected company?',
  DELETE_CONFIRMATION_MULTIPLE: (count: number) =>
    `Are you sure you want to delete ${count} selected companies?`,
  NO_COMPANIES_SELECTED: 'Please select at least one company.',
  LOADING: 'Loading...',
  ERROR: 'An error occurred',
  NO_DATA: 'No data available',
  MORE: 'More >>',
  LESS: 'Less <<',
} as const;

// ============================================================================
// API/DATA PATHS
// ============================================================================

/**
 * Paths to JSON data files (relative to build output)
 */
export const DATA_PATHS = {
  MARCH_2023: '/src/data/march2023.json',
  MARCH_2024: '/src/data/march2024.json',
  MARCH_2023_TRADING: '/src/data/march2023TradingMultiples.json',
  MARCH_2024_TRADING: '/src/data/march2024TradingMultiples.json',
  MARCH_2023_OPERATING: '/src/data/march2023OperatingStats.json',
  MARCH_2024_OPERATING: '/src/data/march2024OperatingStats.json',
  BUSINESS_DESCRIPTIONS: '/src/data/businessDescriptions.json',
  HEADQUARTERS: '/src/data/headquarters.json',
  TARGET_COMPANIES_DATA: '/src/data/targetCompanies.json',
} as const;

// ============================================================================
// IMAGE PATHS
// ============================================================================

/**
 * Paths to financial statement images for different timeline ranges
 */
export const IMAGE_PATHS = {
  // Income Statement - 2019-2025
  INCOME_1925_ANNUAL: ['/img/income1-25.png', '/img/income2-25.png'],
  INCOME_1925_QUARTERLY: ['/img/income1-Q25.png', '/img/income2-Q25.png'],

  // Income Statement - 2020-2023
  INCOME_2023_ANNUAL: ['/img/income1-23.png', '/img/income2-23.png'],
  INCOME_2023_QUARTERLY: ['/img/income1-Q23.png'],

  // Income Statement - 2013-2023 (only Annual)
  INCOME_1323_ANNUAL: ['/img/income-13-23.png'],

  // Balance Sheet - 2019-2025 (no period type variation)
  BALANCE_1925: ['/img/balance1-25.png', '/img/balance2-25.png'],

  // Balance Sheet - 2020-2023
  BALANCE_2023_ANNUAL: ['/img/balance-23.png'],
  BALANCE_2023_QUARTERLY: ['/img/balance1-Q23.png', '/img/balance2-Q23.png'],

  // Balance Sheet - 2013-2023 (only Annual)
  BALANCE_1323_ANNUAL: ['/img/balance-13-23.png'],

  // Cash Flow - 2019-2025 (no period type variation)
  CASH_1925: ['/img/cash1-25.png', '/img/cash2-25.png'],

  // Cash Flow - 2020-2023
  CASH_2023_ANNUAL: ['/img/cash-23.png'],
  CASH_2023_QUARTERLY: ['/img/cash-Q23.png'],

  // Cash Flow - 2013-2023 (only Annual)
  CASH_1323_ANNUAL: ['/img/cash-13-23.png'],
} as const;

// ============================================================================
// DOM SELECTORS FOR CONTENT MANAGEMENT
// ============================================================================

/**
 * Additional DOM selectors for content strategy management
 */
export const DOM_SELECTORS = {
  KEY_FINANCIALS: '.key-financials-wrapper',
  FINANCIALS_TABLE_CONTAINER: '.financials-table-container',
  FOOTNOTE: '.footnote',
  CAPITALIZATION_SECTION: '.capitalization-section',
  VALUATION_SECTION: '.valuation-section',
  MAIN_SECTION: '.main-section',
  MAIN_CONTENT: '.main-content',

  // 2020-2023 specific sections
  CAP_SECTION_2020_2023: '.capitalization-section-2020-2023',
  VAL_SECTION_2020_2023: '.valuation-section-2020-2023',

  // Financial statement sections by range
  INCOME_SECTION_2020_2023: '.income-statement-section-2020-2023',
  INCOME_SECTION_1925: '.income-statement-section-1925',
  INCOME_SECTION_1323: '.income-statement-section-1323',
  BALANCE_SECTION_2020_2023: '.balance-sheet-section-2020-2023',
  BALANCE_SECTION_1925: '.balance-sheet-section-1925',
  BALANCE_SECTION_1323: '.balance-sheet-section-1323',
  CASH_SECTION_2020_2023: '.cash-flow-section-2020-2023',
  CASH_SECTION_1925: '.cash-flow-section-1925',
  CASH_SECTION_1323: '.cash-flow-section-1323',
} as const;

// ============================================================================
// TYPE EXPORTS
// ============================================================================

/**
 * Infer types from constants for type safety
 */
export type MonthValue = typeof MONTHS[number];
export type DataYear = typeof DATA_YEARS[number];
export type TimelineYear = typeof TIMELINE_YEARS[number];
export type PeriodType = typeof PERIOD_TYPES[number];
export type TabId = typeof TAB_IDS[keyof typeof TAB_IDS];
export type TabName = typeof TAB_NAMES[keyof typeof TAB_NAMES];
export type StorageKey = typeof STORAGE_KEYS[keyof typeof STORAGE_KEYS];
export type ToggleState = typeof TOGGLE_STATES[keyof typeof TOGGLE_STATES];
export type ToggleType = typeof TOGGLE_TYPES[keyof typeof TOGGLE_TYPES];
export type SummaryRow = typeof SUMMARY_ROWS[keyof typeof SUMMARY_ROWS];
